import { useEffect, useState, type FormEvent } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { SelectField, TextField } from "@/components/Field";
import { Loaded } from "@/components/Loading";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { call, CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import { LOCALE_OPTIONS } from "@/i18n";
import {
  retentionVerdict,
  RETENTION_MAX_DAYS,
  RETENTION_MIN_DAYS,
} from "./retention";
import type {
  AssessmentPreset,
  BillingStatus,
  Institution,
  Locale,
  SessionMode,
} from "@/types";

/**
 * What an owner may change about their own institution.
 *
 * Country is shown and not editable, with the reason next to it: country
 * decides which legal documents apply, so changing it would silently change
 * what the institution is required to have signed. That is an admin action with
 * a conversation attached, not a dropdown.
 */

interface Draft {
  name: string;
  segment: Institution["segment"];
  locale: Locale;
  defaultSessionMode: SessionMode;
  assessmentPreset: AssessmentPreset;
  retentionOperationalDays: number;
}

function draftFrom(institution: Institution, retentionDays: number): Draft {
  return {
    name: institution.name,
    segment: institution.segment,
    locale: institution.locale,
    defaultSessionMode: institution.defaultSessionMode,
    assessmentPreset: institution.assessmentPreset,
    retentionOperationalDays: retentionDays,
  };
}

export function InstitutionSettingsPage() {
  const t = useT();
  const { locale } = useI18n();
  const { institution } = useAuth();
  const { success, showError } = useToast();

  const billing = useCallable<BillingStatus>(CALLABLES.getBillingStatus);

  const [draft, setDraft] = useState<Draft | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // The institution arrives from a live subscription, so the form is seeded
  // once it does. Re-seeding on every snapshot would take the field out from
  // under someone typing in it.
  useEffect(() => {
    if (institution && !draft) {
      setDraft(draftFrom(institution, institution.retention.operationalDays));
    }
  }, [institution, draft]);

  if (!institution || !draft) {
    return (
      <SectionShell titleKey="institution.title">
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      </SectionShell>
    );
  }

  const verdict = retentionVerdict(draft.retentionOperationalDays);
  const retentionOutOfRange = verdict === "outOfRange";

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (retentionOutOfRange) return;

    setBusy(true);
    try {
      await call(CALLABLES.updateInstitution, {
        institutionId: institution.id,
        name: draft.name.trim(),
        segment: draft.segment,
        locale: draft.locale,
        defaultSessionMode: draft.defaultSessionMode,
        assessmentPreset: draft.assessmentPreset,
        retentionOperationalDays: draft.retentionOperationalDays,
      });
      success("institution.saved");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell titleKey="institution.title">
      <form className="flex max-w-2xl flex-col gap-10" onSubmit={onSubmit}>
        <section className="flex flex-col gap-4">
          <h2 className="section-title">{t("institution.section.identity")}</h2>

          <TextField
            labelKey="institution.name"
            required
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />

          <div>
            <p className="field-label">{t("institution.country")}</p>
            <p className="field-input bg-surface-muted text-ink-muted">{institution.country}</p>
            <p className="mt-1.5 text-sm text-ink-muted">{t("institution.countryFixed")}</p>
          </div>

          <SelectField
            labelKey="institution.segment"
            value={draft.segment}
            onChange={(event) =>
              setDraft({ ...draft, segment: event.target.value as Institution["segment"] })
            }
            options={[
              { value: "studio", labelKey: "institution.segment.studio" },
              { value: "school", labelKey: "institution.segment.school" },
            ]}
          />

          <SelectField
            labelKey="institution.locale"
            hintKey="institution.localeHint"
            value={draft.locale}
            onChange={(event) => setDraft({ ...draft, locale: event.target.value as Locale })}
            options={LOCALE_OPTIONS}
          />
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="section-title">{t("institution.section.defaults")}</h2>

          <SelectField
            labelKey="institution.defaultSessionMode"
            hintKey="institution.modeHint"
            value={draft.defaultSessionMode}
            onChange={(event) =>
              setDraft({ ...draft, defaultSessionMode: event.target.value as SessionMode })
            }
            options={[
              { value: "online", labelKey: "institution.mode.online" },
              { value: "offline", labelKey: "institution.mode.offline" },
            ]}
          />

          <SelectField
            labelKey="institution.assessmentPreset"
            value={draft.assessmentPreset}
            onChange={(event) =>
              setDraft({ ...draft, assessmentPreset: event.target.value as AssessmentPreset })
            }
            options={[
              { value: "epa", labelKey: "institution.preset.epa" },
              { value: "aoao", labelKey: "institution.preset.aoao" },
              { value: "ncas", labelKey: "institution.preset.ncas" },
              { value: "custom", labelKey: "institution.preset.custom" },
            ]}
          />
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="section-title">{t("institution.section.retention")}</h2>

          <TextField
            labelKey="institution.retentionDays"
            hintKey="institution.retentionHint"
            type="number"
            min={RETENTION_MIN_DAYS}
            max={RETENTION_MAX_DAYS}
            required
            value={String(draft.retentionOperationalDays)}
            errorKey={retentionOutOfRange ? "error.validation.retentionOutOfRange" : undefined}
            errorParams={{ min: RETENTION_MIN_DAYS, max: RETENTION_MAX_DAYS }}
            onChange={(event) =>
              setDraft({ ...draft, retentionOperationalDays: Number(event.target.value) })
            }
          />

          {verdict === "short" ? (
            <p className="rounded-card border border-warning bg-warning-soft px-4 py-3 text-sm text-ink-soft">
              {t("institution.retentionShortWarning")}
            </p>
          ) : null}
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="section-title">{t("institution.section.billing")}</h2>
          <Loaded state={billing}>
            {(status) => (
              <VatPanel status={status} locale={locale} onChanged={billing.reload} />
            )}
          </Loaded>
        </section>

        <div>
          <Button type="submit" busy={busy} disabled={retentionOutOfRange}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </SectionShell>
  );
}

/**
 * The VAT identification number and what it means for the invoice.
 *
 * Shown with its validation state rather than as a free text field, because an
 * unvalidated number looks exactly like an entitlement to zero-rating and is
 * not one. The backend refuses to store a number VIES does not recognise; this
 * says so before anyone wonders why it did not save.
 */
function VatPanel({
  status,
  locale,
  onChanged,
}: {
  status: BillingStatus;
  locale: Locale;
  onChanged: () => void;
}) {
  const t = useT();
  const { success, showError } = useToast();
  const [vatId, setVatId] = useState(status.vatId ?? "");
  const [busy, setBusy] = useState(false);

  const treatmentKey =
    status.vatTreatment === "reverse_charge"
      ? "institution.vat.validated"
      : status.vatTreatment === "outside_scope"
        ? "institution.vat.outsideScope"
        : status.vatId
          ? "institution.vat.charge"
          : "institution.vat.none";

  const submit = async (action: "set" | "clear") => {
    setBusy(true);
    try {
      if (action === "set") await call(CALLABLES.setVatId, { vatId: vatId.trim() });
      else {
        await call(CALLABLES.clearVatId);
        setVatId("");
      }
      success("institution.saved");
      onChanged();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <TextField
        labelKey="institution.vatId"
        hintKey="institution.vatIdHint"
        value={vatId}
        autoCapitalize="characters"
        spellCheck={false}
        onChange={(event) => setVatId(event.target.value)}
      />

      <p className="text-sm text-ink-muted">
        {t(treatmentKey, {
          date: status.vatIdValidatedAt ? formatDate(locale, status.vatIdValidatedAt) : "",
        })}
      </p>

      <div className="flex gap-2">
        <Button
          variant="secondary"
          busy={busy}
          disabled={vatId.trim().length === 0}
          onClick={() => submit("set")}
        >
          {t("institution.vat.revalidate")}
        </Button>
        {status.vatId ? (
          <Button variant="ghost" busy={busy} onClick={() => submit("clear")}>
            {t("institution.vat.remove")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
